import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
  CircularProgress,
  Grid
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { RoleContext } from 'src/context/roleContext';
import { cyrillicToLatin, decodeUnicode } from 'src/helper/helperFunction';
import DateRangeIcon from '@mui/icons-material/DateRange';
import dayjs from 'dayjs';
import { ImageUrl } from 'src/constants/imagesConst';
import { getSchoolInfo } from 'src/api/schoolMetod';
import { SKOLA } from 'src/constants/role';
import SuccessDialog from 'src/components/dialogs/success/SuccessDialog';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import { FLAG_ZALBE } from 'src/constants/competitions';
import { useLocation } from 'react-router-dom';

/* SKOLA STRANA ZA KREIRANJE NOVE ZALBE */

const CreateComplaint = () => {
  const { role, schoolId, userId } = useContext(RoleContext);
  const { activeModes } = useContext(ActiveModesContext);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const location = useLocation();
  const { studentId } = location.state || {};

  const [students, setStudents] = useState([]);
  const [professors, setProfessors] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [category, setCategory] = useState('');
  const [complaintMessage, setComplaintMessage] = useState('');
  const [error, setError] = useState(null);
  const [date, setDate] = useState(dayjs().format('DD.MM.YYYY'));
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState('');
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [submissionDate, setSubmissionDate] = useState('');
  const [schooluserName, setSchoolUserName] = useState('');

  useEffect(() => {
    setSchoolUserName(localStorage.getItem('userID').slice(1, -1));
  }, []);

  useEffect(() => {
    const fetchStudentsAndProfessors = async () => {
      try {
        const studentsUrl = schoolId
          ? `${API_BASE_URL}/api/students.php?get_all_school_id=${schoolId[0]}`
          : `${API_BASE_URL}/api/students.php`;

        const response = await axios.get(studentsUrl);
        const decodedStudents = response.data.map((student) => ({
          ...student,
          id: parseInt(student.id, 10),
          name: decodeUnicode(student.name),
          professor: decodeUnicode(student.professor),
          gender: decodeUnicode(student.gender),
          language: decodeUnicode(student.language),
          category: decodeUnicode(student.category)
        }));

        const complaintsUrl = `${API_BASE_URL}/api/complaints.php?schoolId=${schoolId[0]}&grouped=true`;
        const complaintsResponse = await axios.get(complaintsUrl);
        const complaints = complaintsResponse.data;

        // Lista ID-ova učenika koji imaju žalbe
        const studentIdsWithComplaints = complaints.map((complaint) =>
          parseInt(complaint.id_ucenika, 10)
        );

        const studentsWithoutComplaints = decodedStudents.filter(
          (student) =>
            !studentIdsWithComplaints.includes(student.id) &&
            student.result !== '0' &&
            student.result !== ''
        );

        setStudents(studentsWithoutComplaints);

        if (studentId) {
          const studentHasComplaint = studentIdsWithComplaints.includes(
            parseInt(studentId, 10)
          );

          if (studentHasComplaint) {
            setError(t('Žalba je već podneta za ovog učenika.'));
          } else {
            const selected = studentsWithoutComplaints.find(
              (student) => student.id === parseInt(studentId, 10)
            );
            if (selected) {
              setSelectedStudent(selected);
            } else {
              setError(t('Izabrani učenik nije pronađen.'));
            }
          }
        }

        const uniqueProfessors = [
          ...new Set(
            studentsWithoutComplaints.map((student) => student.professor)
          )
        ];
        setProfessors(uniqueProfessors);

        setLoading(false);
      } catch (error) {
        console.error('Greška prilikom preuzimanja učenika ili žalbi:', error);
        setError(t('error.fetchingStudents'));
        setLoading(false);
      }
    };

    fetchStudentsAndProfessors();
  }, [schoolId, t, studentId]);

  useEffect(() => {
    const fetchSchoolName = async () => {
      if (role === SKOLA) {
        const id = userId.match(/\d+/);
        if (id) {
          try {
            const data = await getSchoolInfo(id[0]);
            setSchoolName(data['name']);
          } catch (error) {
            console.error(
              'Greška prilikom preuzimanja informacija o školi:',
              error
            );
          }
        }
      }
    };

    fetchSchoolName();
  }, [role, userId]);

  useEffect(() => {
    if (selectedStudent) {
      setSelectedProfessor(selectedStudent.professor);
      setCategory(selectedStudent.category);
    } else {
      setSelectedProfessor('');
      setCategory('');
    }
  }, [selectedStudent]);

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleViewComplaints = () => {
    handleCloseSuccessDialog();
    const url = `https://imi.pmf.kg.ac.rs/kengur2/#/complaints/${schoolId[0]}`;
    window.open(url, '_blank');
    // window.location.href = `/kengur2/complaints/${schoolId[0]}`;
  };

  const handleSubmit = async () => {
    if (!selectedStudent || !complaintMessage) {
      setError(t('error.missingFields'));
      return;
    }

    const complaintData = {
      id_skole: schoolId[0],
      id_ucenika: selectedStudent.id,
      poruku_poslao: schooluserName,
      poruka: complaintMessage,
      status: 0,
      skola_procitao: 1,
      admin_procitao: 0,
      date: new Date().toISOString().slice(0, 10)
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/complaints.php`,
        complaintData
      );
      if (response.data.success) {
        const now = new Date().toLocaleDateString();
        setSubmissionDate(now);
        setOpenSuccessDialog(true);

        setSelectedStudent(null);
        setSelectedProfessor('');
        setCategory('');
        setComplaintMessage('');

        // Osvježite listu učenika
        const updatedStudents = students.filter(
          (student) => student.id !== selectedStudent.id
        );
        setStudents(updatedStudents);
      } else if (response.data.error) {
        if (
          response.data.error === 'Complaint already exists for this student'
        ) {
          setError(t('error.complaintAlreadyExists'));
        } else {
          setError(response.data.error);
        }
      }
    } catch (error) {
      console.error('Greška prilikom podnošenja žalbe:', error);
      setError(t('error.submittingComplaint'));
    }
  };

  if (!activeModes.includes(FLAG_ZALBE)) {
    return (
      <Box
        display="grid"
        gap={3}
        p={2}
        sx={{
          backgroundColor: '#e0f7fa',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {t('complaints.noCreateComplaints')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      m="20px"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: colors.background,
        boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `url(${ImageUrl.mathBackground}) no-repeat center center`,
          backgroundSize: 'cover',
          opacity: 0.15,
          zIndex: -1
        }
      }}
    >
      <Header
        title={t('dialog.submitComplaint')}
        sx={{
          color: colors.primary[700],
          fontWeight: 'bold'
        }}
      />
      <Box
        component="form"
        sx={{
          backgroundColor: colors.primary[400],
          padding: '30px',
          borderRadius: '16px',
          boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '800px',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
        noValidate
        autoComplete="off"
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: colors.primary[800],
            marginBottom: 3,
            textAlign: 'center'
          }}
        >
          {t('dialog.formTitle')}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            {error && (
              <Typography
                sx={{
                  color: 'error.main',
                  backgroundColor: '#ffe6e6',
                  padding: '10px',
                  borderRadius: '8px'
                }}
              >
                {error}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 2
              }}
            >
              <DateRangeIcon sx={{ marginRight: 1, color: colors.grey[600] }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: colors.grey[600]
                }}
              >
                {t('dialog.dateLabel')}: {date}
              </Typography>
            </Box>
            <TextField
              label={t('dialog.schoolLabel')}
              value={schoolName}
              fullWidth
              disabled
              variant="outlined"
              size="small"
              sx={{ marginBottom: 2 }}
            />
            <Autocomplete
              options={students}
              getOptionLabel={(option) => option.name || ''}
              value={selectedStudent}
              onChange={(event, newValue) => setSelectedStudent(newValue)}
              filterOptions={(options, { inputValue }) => {
                const searchValue = cyrillicToLatin(inputValue.toLowerCase());
                return options.filter((option) =>
                  cyrillicToLatin(option.name.toLowerCase()).includes(
                    searchValue
                  )
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              noOptionsText={t('pagination.noRowsLabel')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('dialog.studentNameLabel')}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ marginBottom: 2 }}
                />
              )}
            />

            <Grid container spacing={3} sx={{ marginBottom: 2 }}>
              <Grid item xs={6}>
                <TextField
                  label={t('dialog.classLabel')}
                  value={selectedStudent ? selectedStudent.class : ''}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('dialog.categoryLabel')}
                  value={category || ''}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
            </Grid>

            <TextField
              label={t('dialog.professorLabel')}
              value={selectedProfessor || ''}
              fullWidth
              variant="outlined"
              size="small"
              disabled
              sx={{ marginBottom: 2 }}
            />

            <TextField
              label={t('dialog.complaintMessageLabel')}
              value={complaintMessage}
              onChange={(e) => setComplaintMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              size="small"
              sx={{ marginBottom: 2 }}
            />
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#2596be',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 20px',
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(37, 150, 190, 0.4)',
                '&:hover': {
                  backgroundColor: '#1b7c99',
                  boxShadow: '0px 6px 16px rgba(27, 124, 153, 0.6)'
                },
                transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
              }}
              onClick={handleSubmit}
              disabled={loading || students.length === 0 || !!error}
            >
              {t('dialog.submitButton')}
            </Button>
          </>
        )}
      </Box>

      <SuccessDialog
        open={openSuccessDialog}
        onClose={handleCloseSuccessDialog}
        onViewComplaints={handleViewComplaints}
        submissionDate={submissionDate}
      />
    </Box>
  );
};

export default CreateComplaint;
